import ApiService from './api';

const ProductAdvisorService = {
  /**
   * Fetches all product advisors for the bot by ID, with all products populated in each advisor
   * @param botId Bot whose advisors are fetched
   * @returns Advisor documents, or `null` if an error occurred
   */
  async getAdvisors(botId: string): Promise<any[] | null> {
    try {
      const r = await ApiService.get(`/schaltzentrale/product-advisor/bot/${botId}/advisors`);
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * Creates a new Product Advisor, along with its related intent
   * @param botId Bot the advisor is created for
   * @param intentPayload Intent creation payload for the advisor's related intent (shape defined by the intent editor)
   * @param name Display name of the advisor
   * @param description Purpose description of the advisor
   * @param mock When true, returns a hardcoded sample response instead of calling the backend
   */
  async createAdvisor(
    botId: string,
    intentPayload: any,
    name: string,
    description: string,
    mock: boolean = false
  ): Promise<any> {
    if (mock === true) {
      return {
        "advisor": {
          "_id": "664cc710f2ac6f87a05198bc",
          "botId": "aBVwAoAF",
          "intent": "faq_test_f",
          "purpose": "Testin again, test F",
          "company": "Waterkant Touren",
          "features": [],
          "__v": 0
        },
        "intent": {
          "createdBy": {
            "user": "martin.haslien@moin.ai"
          },
          "translation": {
            "de": {
              "displayName": "Prüfung F",
              "description": "Erneut testen, Test F"
            },
            "en": {
              "displayName": "Test F",
              "description": "Testin again, test F"
            }
          },
          "staticIntentSuggestions": [],
          "k2names": [],
          "_id": "664cc70ef2ac6f87a051989b",
          "relations": [],
          "intent": "faq_test_f",
          "displayName": "Test F",
          "template": "ProductAdvisor",
          "description": "Testin again, test F",
          "box": "faq_test_f",
          "botId": "aBVwAoAF",
          "language": "de",
          "createdAt": "2024-05-21T16:08:47.515Z",
          "updatedAt": "2024-05-21T16:08:47.515Z",
          "__v": 0,
          "samples": []
        }
      };
    }

    try {
      const r = await ApiService.post(`/schaltzentrale/product-advisor/bot/${botId}/advisors`, {
        name,
        description,
        intent: intentPayload,
      });
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * Overwrites one or more fields of the product advisor document
   * @param botId Bot the advisor belongs to
   * @param advisorId Advisor document to patch
   * @param newFields Partial advisor fields to overwrite
   * @returns New state of the advisor. It does not contain products. `null` on error.
   */
  async updateAdvisor(
    botId: string,
    advisorId: string,
    newFields: any
  ): Promise<any> {
    try {
      const r = await ApiService.patch(`/schaltzentrale/product-advisor/bot/${botId}/advisors/${advisorId}`, newFields);
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * Creates a new product for the given advisor
   * @param botId Bot the advisor belongs to
   * @param advisorId Advisor the product is added to
   * @param productPayload Product document to create (shape defined by the product editor)
   */
  async createProduct(
    botId: string,
    advisorId: string,
    productPayload: any
  ): Promise<any> {
    try {
      const r = await ApiService.post(`/schaltzentrale/product-advisor/bot/${botId}/advisors/${advisorId}/products`, productPayload);
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },

  /**
   * Update a product
   * @param botId Bot the advisor belongs to
   * @param advisorId Advisor owning the product
   * @param productId Product document to patch
   * @param newFields Partial product fields to overwrite
   */
  async updateProduct(
    botId: string,
    advisorId: string,
    productId: string,
    newFields: any
  ): Promise<any> {
    try {
      const r = await ApiService.patch(`/schaltzentrale/product-advisor/bot/${botId}/advisors/${advisorId}/products/${productId}`, newFields);
      return r.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  /**
   * Delete a product
   * @param botId Bot the advisor belongs to
   * @param advisorId Advisor owning the product
   * @param productId Product document to delete
   */
  async deleteProduct(
    botId: string,
    advisorId: string,
    productId: string
  ): Promise<void> {
    try {
      await ApiService.delete(`/schaltzentrale/product-advisor/bot/${botId}/advisors/${advisorId}/products/${productId}`);
    } catch (e) {
      console.error(e);
    }
  },
};

export default ProductAdvisorService;
export { ProductAdvisorService };
